import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModuleService } from '../../../services/module.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { CreateModuleRequest, UpdateModuleRequest, Module } from '../../../models/module.model';
import { MaterialModule } from '../../../shared/material.module';

export enum DialogMode {
  CREATE = 'create',
  EDIT = 'edit',
  VIEW = 'view'
}

export interface ModuleDialogData {
  mode: DialogMode;
  module?: Module;
}

@Component({
  selector: 'app-module-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './module-dialog.component.html',
  styleUrl: './module-dialog.component.scss'
})
export class ModuleDialogComponent implements OnInit {
  moduleForm: FormGroup;
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private moduleService: ModuleService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService,
    public dialogRef: MatDialogRef<ModuleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModuleDialogData
  ) {
    this.moduleForm = this.fb.group({
      name: ['', [Validators.required]],
      slug: ['', [Validators.required]],
      description: [''],
      icon: [''],
      color: [''],
      version: [''],
      status: ['active'],
      type: [''],
      isCore: [false]
    });
  }

  ngOnInit(): void {
    if (this.data.module) {
      this.moduleForm.patchValue(this.data.module);
      
      if (this.isReadOnly) {
        this.moduleForm.disable();
      }
    }
  }

  get dialogTitle(): string {
    switch (this.data.mode) {
      case DialogMode.CREATE:
        return 'Create New Module';
      case DialogMode.EDIT:
        return 'Edit Module';
      case DialogMode.VIEW:
        return 'View Module';
      default:
        return 'Module';
    }
  }

  get isReadOnly(): boolean {
    return this.data.mode === DialogMode.VIEW;
  }

  onSave(): void {
    if (this.moduleForm.valid) {
      this.isLoading = true;
      
      if (this.data.mode === DialogMode.EDIT && this.data.module) {
        this.updateModule();
      } else if (this.data.mode === DialogMode.CREATE) {
        this.createModule();
      }
    }
  }

  private createModule(): void {
    const createData: CreateModuleRequest = this.moduleForm.value;
    this.moduleService.createModule(createData).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastService.success('Module Created', 'Module has been created successfully');
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (error: any) => {
        this.isLoading = false;
        const errorResult = this.errorHandler.handleError(error, this.moduleForm);
        this.toastService.error('Error', errorResult.message);
        
        // Show unmapped validation errors if any
        const unmappedErrors = this.errorHandler.getUnmappedValidationErrors(errorResult.validationErrors || [], this.moduleForm);
        if (unmappedErrors.length > 0) {
          const errorMessages = unmappedErrors.map(e => `${e.field}: ${e.message}`).join(', ');
          this.toastService.error('Validation Errors', errorMessages);
        }
      }
    });
  }

  private updateModule(): void {
    const updateData: UpdateModuleRequest = this.moduleForm.value;
    this.moduleService.updateModule(this.data.module!.id, updateData).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastService.success('Module Updated', 'Module has been updated successfully');
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (error: any) => {
        this.isLoading = false;
        const errorResult = this.errorHandler.handleError(error, this.moduleForm);
        this.toastService.error('Error', errorResult.message);
        
        // Show unmapped validation errors if any
        const unmappedErrors = this.errorHandler.getUnmappedValidationErrors(errorResult.validationErrors || [], this.moduleForm);
        if (unmappedErrors.length > 0) {
          const errorMessages = unmappedErrors.map(e => `${e.field}: ${e.message}`).join(', ');
          this.toastService.error('Validation Errors', errorMessages);
        }
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}